// The Trace PRESENTATION vocabulary — how one Trace entry reads on ANY
// surface (web dashboard, desktop miniapp, the future RN app): the
// kind→label/icon/tone mapping, the time/cost formatting, and the Explain
// ask composer. Platform-neutral and pure (no `window`, no clock reads
// except by injectable default), so every surface renders the Trace ONE way
// and the phone inherits it the way it inherits chat (Brief 3 D6).
//
// The Trace contract (`TraceEvent`) speaks the backend's wire shape: `kind`
// is an OPEN vocabulary (the known kinds are commit | proof | operator_flag
// | operator_halt | suggestion | reach), and an unknown kind must still
// render — never throw, never blank. `created_at` is micros since unix
// epoch; `cost_micros` is a spend in micros, shown only when > 0.

import type { TraceEvent, TraceStreamEvent } from "./types";

// ─── Kind → presentation (label · icon · tone) ─────────────────────────
// Each known kind maps to a glanceable chip: a short label, a single glyph,
// and a TONE that colours it (the same scarce palette the Operators use):
//   - commit       → neutral (work landed)
//   - proof ✓      → success/green (a claim was verified — the good signal)
//   - operator_flag→ Supervisor's warm guard accent (Supervisor raised a flag)
//   - operator_halt→ danger/red (Supervisor stopped the work — the loudest signal)
//   - suggestion   → the arbiters' shared cool accent (an arbiter offered a
//                    better way; the trace kind doesn't say which of the
//                    three, and all three wear one hue)
//   - reach        → Signal's signal accent (Signal reached the Orchestrator)
// An unknown kind falls back to a neutral dot + the raw kind as its label,
// so a kind the backend adds later still renders honestly.

/** The semantic tone of a Trace chip — a key the view maps to a text-colour
 * class. Kept as a closed enum (not a raw class) so the model never hard-
 * codes styling and each surface owns its token mapping. */
export type TraceTone =
  | "neutral"
  | "success"
  | "supervisor"
  | "danger"
  // ONE shared tone for all three domain arbiters: a trace `suggestion` /
  // `corpus` kind carries no arbiter attribution, and the three arbiters
  // share one accent hue anyway.
  | "arbiter"
  | "signal";

/** How one Trace `kind` presents: a short chip label, a single glyph, and a
 * tone. Pure data — the view turns `tone` into a colour class. */
export interface TracePresentation {
  /** Short, human chip label (e.g. "commit", "proof", "halt"). */
  label: string;
  /** A single-glyph icon for the chip (text, not an SVG dependency). */
  icon: string;
  tone: TraceTone;
}

/** The known kinds' presentation, keyed by the wire `kind`. Unknown kinds
 * are handled by `tracePresentation`, not here. */
const KIND_PRESENTATION: Record<string, TracePresentation> = {
  commit: { label: "commit", icon: "◆", tone: "neutral" },
  proof: { label: "proof", icon: "✓", tone: "success" },
  operator_flag: { label: "flag", icon: "⚑", tone: "supervisor" },
  operator_halt: { label: "halt", icon: "■", tone: "danger" },
  suggestion: { label: "suggestion", icon: "◇", tone: "arbiter" },
  reach: { label: "reach", icon: "↗", tone: "signal" },
  // T2 provenance: the corpus patterns an arbiter drew on for a suggestion.
  corpus: { label: "corpus", icon: "◈", tone: "arbiter" },
};

/**
 * The chip presentation for a Trace `kind`. Total: a known kind returns its
 * mapped label/icon/tone; an unknown/garbled kind falls back to a neutral
 * dot with the raw kind string as its label (or "event" when the kind is
 * blank), so the timeline always renders a kind a future backend adds
 * without a code change.
 */
export function tracePresentation(kind: string): TracePresentation {
  const known = KIND_PRESENTATION[kind];
  if (known) return known;
  const label = kind.trim() === "" ? "event" : kind;
  return { label, icon: "•", tone: "neutral" };
}

// ─── Time + cost formatting ────────────────────────────────────────────

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * Format a Trace entry's `created_at` (micros since epoch) as a short
 * relative label ("now" / "2m" / "1h" / "3d"). Returns "" when the time is
 * missing/garbled so the caller can omit the slot. `now` is injectable for
 * deterministic tests; it defaults to wall-clock millis.
 */
export function formatTraceWhen(
  createdAtMicros: number,
  now: number = Date.now(),
): string {
  if (!Number.isFinite(createdAtMicros) || createdAtMicros <= 0) return "";
  const ms = Math.floor(createdAtMicros / 1000);
  const diff = now - ms;
  // Future or sub-minute reads as "now" — avoids a jittery "0m" / "-1m".
  if (diff < MINUTE_MS) return "now";
  if (diff < HOUR_MS) return `${Math.floor(diff / MINUTE_MS)}m`;
  if (diff < DAY_MS) return `${Math.floor(diff / HOUR_MS)}h`;
  return `${Math.floor(diff / DAY_MS)}d`;
}

/**
 * Format an entry's `created_at` (micros) as an absolute UTC timestamp for
 * the title/hover slot, e.g. "2026-06-08 14:32 UTC". UTC by construction
 * (read off `toISOString()`) so it's host-timezone-stable. Returns "" when
 * the time is missing/garbled.
 */
export function formatTraceAbsolute(createdAtMicros: number): string {
  if (!Number.isFinite(createdAtMicros) || createdAtMicros <= 0) return "";
  const iso = new Date(Math.floor(createdAtMicros / 1000)).toISOString();
  // "2026-06-08T14:32:10.000Z" → "2026-06-08 14:32 UTC"
  return `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC`;
}

/**
 * Format a `cost_micros` spend as a compact label: the base unit with up to
 * two decimals, dropping a trailing ".00" / ".x0". Returns "" for a
 * non-positive/garbled cost so the caller omits the slot (the contract:
 * show cost ONLY when > 0).
 */
export function formatTraceCost(costMicros: number): string {
  if (!Number.isFinite(costMicros) || costMicros <= 0) return "";
  const base = costMicros / 1_000_000;
  const rounded = Math.round(base * 100) / 100;
  const text = Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toFixed(2).replace(/0$/, "");
  return text;
}

// ─── The intervention cut (Brief 3 D4) ──────────────────────────────────

/** Whether a Trace kind is an Operator INTERVENTION — something an Operator
 * DID to the session (a flag raised, a halt, a reach to the human) — as
 * opposed to informational record (suggestions, commits, proofs, costs,
 * narration). The "should I step in?" question reads off exactly this cut;
 * one vocabulary with the presentation above, never a second taxonomy. PURE. */
export function isIntervention(kind: string): boolean {
  return kind === "operator_flag" || kind === "operator_halt" || kind === "reach";
}

// ─── The live stream → timeline merge ──────────────────────────────────

/** A live `trace` frame as a renderable `TraceEvent` (drop the routing
 * `thread_id`). The live frame's id is 0; the durable id arrives on the next
 * full read. PURE. */
export function traceEventFromStream(frame: TraceStreamEvent): TraceEvent {
  return {
    id: frame.id,
    kind: frame.kind,
    summary: frame.summary,
    evidence_ref: frame.evidence_ref,
    cost_micros: frame.cost_micros,
    created_at: frame.created_at,
  };
}

// ─── Explain (Brief 3 D6) ───────────────────────────────────────────────

/** Compose the ASIDE body for one Trace entry's Explain ask: a quoted line
 * (kind label, absolute time, evidence ref, summary) + Signal's question.
 * An aside is product-side-only — the bridge never delivers it to the
 * Agent — so this is Orchestrator-to-Signal talk and names it freely. */
export function explainAskBody(event: TraceEvent): string {
  const p = tracePresentation(event.kind);
  const when = formatTraceAbsolute(event.created_at);
  const parts = [p.label, when, event.evidence_ref].filter(Boolean).join(", ");
  return (
    `Signal, explain this Trace point:\n` +
    `> [${parts}] ${event.summary || "(no summary)"}\n` +
    `What happened here, and does it need me?`
  );
}
